import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReusableTable, { TableColumn, TableAction, DropdownItem } from '../components/adminTable';
import { Button } from '../components/adminModals';
import ReportActionModal from '../components/ReportActionModal';
import Pagination from '../components/Pagination';
import { useNotification } from '../../sharedComponents/Notifications';
import { useAuth } from '../../../auth/hooks/auth';
import {
    reportBatchService,
    ReportBatchItem,
    BatchStatus,
    APPROVAL_TYPE_LABELS,
    BATCH_STATUS_LABELS,
} from '../services/adminAuth';
import { Plus, Eye, SendHorizontal, Pencil } from 'lucide-react';

// ── Status badge ────────────────────────────────────────────────────────────────

const batchStatusStyle = (status: number): string => {
    switch (status) {
        case BatchStatus.Draft: return 'bg-gray-100 text-gray-700';
        case BatchStatus.PendingApproval: return 'bg-yellow-50 text-yellow-700';
        case BatchStatus.Approved: return 'bg-green-50 text-green-700';
        case BatchStatus.ReadyForSubmission: return 'bg-blue-50 text-blue-700';
        case BatchStatus.Rejected: return 'bg-red-50 text-red-700';
        default: return 'bg-gray-100 text-gray-700';
    }
};

const BatchStatusBadge: React.FC<{ status: number }> = ({ status }) => (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${batchStatusStyle(status)}`}>
        {BATCH_STATUS_LABELS[status] || 'Unknown'}
    </span>
);

// Drafts are still editable; only a finalized (ready-for-submission) batch can be submitted for approval.
const isDraft = (status: number) => status === BatchStatus.Draft;
const isReadyForSubmission = (status: number) => status === BatchStatus.ReadyForSubmission;

function ApprovalRequests() {
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    const { user } = useAuth();

    // The TAG Chairman can build drafts and mark them ready, but not submit for approval.
    const isTagChairman = user?.roleId === 5;

    // ── Table state ──────────────────────────────────────────────────────────────
    const [batches, setBatches] = useState<ReportBatchItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    // null → "All Statuses" (the API returns everything); a number filters by that BatchStatus.
    const [statusFilter, setStatusFilter] = useState<number | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // ── Submit confirm state ─────────────────────────────────────────────────────
    const [submitTarget, setSubmitTarget] = useState<ReportBatchItem | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // ── Fetch batches ────────────────────────────────────────────────────────────
    const fetchBatches = async () => {
        setLoading(true);
        try {
            const response = await reportBatchService.getReportBatch({
                pageNumber,
                pageSize,
                sortBy: 'CreatedDate',
                sortDescending: true,
                search,
                batchStatus: statusFilter,
            });
            if (response.isSuccess) {
                setBatches(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch report batches' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching report batches' });
        } finally {
            setLoading(false);
        }
    };

    // Debounced fetch keyed on the actual query params (not the function identity),
    // so it only re-runs when a query input actually changes.
    useEffect(() => {
        const handler = setTimeout(() => { fetchBatches(); }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize, search, statusFilter]);


    // ── Submit for approval ──────────────────────────────────────────────────────
    const handleConfirmSubmit = async () => {
        if (!submitTarget) return;
        setSubmitting(true);
        try {
            const response = await reportBatchService.submitBatchForApproval(submitTarget.id);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Batch submitted for approval successfully', timer: 3000 });
                setSubmitTarget(null);
                await fetchBatches();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to submit batch for approval' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while submitting the batch' });
        } finally {
            setSubmitting(false);
        }
    };

    // View / Update open dedicated full pages, passing the batch through route state.
    const goToView = (batch: ReportBatchItem) => navigate(`/admin/approval-requests/report/${batch.id}`, { state: { batch } });
    const goToUpdate = (batch: ReportBatchItem) => navigate(`/admin/approval-requests/${batch.id}/update`, { state: { batch } });

    // ── Table columns ────────────────────────────────────────────────────────────
    const columns: TableColumn<ReportBatchItem>[] = [
        {
            key: 'sn',
            label: 'S/N',
            sortable: false,
            render: (_v, _row, index) => <span className="text-gray-700">{(pageNumber - 1) * pageSize + (index ?? 0) + 1}</span>,
        },
        {
            key: 'title',
            label: 'Title',
            className: 'font-medium',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{value || APPROVAL_TYPE_LABELS[row.reportType] || 'Report Batch'}</div>
                    <div className="text-xs text-gray-500">{APPROVAL_TYPE_LABELS[row.reportType] || ''}</div>
                </div>
            ),
        },
        {
            key: 'periodLabel',
            label: 'Period',
            sortable: false,
            render: (value, row) => (
                <span className="text-gray-700">
                    {value || `${new Date(row.startDate).toLocaleDateString()} - ${new Date(row.endDate).toLocaleDateString()}`}
                </span>
            ),
        },
        {
            key: 'totalRecords',
            label: 'Records',
            render: (value) => <span className="text-gray-700">{value}</span>,
        },
        {
            key: 'generatedBy',
            label: 'Generated By',
            render: (value) => <span className="text-gray-700">{value || '—'}</span>,
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => <BatchStatusBadge status={value as number} />,
        },
        {
            key: 'createdDate',
            label: 'Created',
            render: (value) => new Date(value).toLocaleDateString(),
        },
    ];

    const getActions = (row: ReportBatchItem): TableAction<ReportBatchItem>[] => {
        const items: DropdownItem<ReportBatchItem>[] = [
            {
                icon: <Eye size={16} />,
                label: 'View Batch',
                onClick: (r: ReportBatchItem) => goToView(r),
                className: 'text-blue-600',
            },
        ];
        // Drafts are still editable — you edit them, then finalize (mark ready) from the edit page.
        if (isDraft(row.status)) {
            items.push({
                icon: <Pencil size={16} />,
                label: 'Edit Draft',
                onClick: (r: ReportBatchItem) => goToUpdate(r),
                className: 'text-blue-600',
            });
        }
        // Only a finalized (ready-for-submission) batch can be submitted for approval,
        // and the TAG Chairman is not allowed to submit.
        if (isReadyForSubmission(row.status) && !isTagChairman) {
            items.push({
                icon: <SendHorizontal size={16} />,
                label: 'Submit for Approval',
                onClick: (r: ReportBatchItem) => setSubmitTarget(r),
                className: 'text-green-600',
            });
        }
        return [{ variant: 'dropdown', onClick: () => undefined, title: 'Actions', dropdownItems: items }];
    };

    // ── Filter panel ─────────────────────────────────────────────────────────────
    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={statusFilter === null ? '' : statusFilter.toString()}
                        onChange={(e) => {
                            setStatusFilter(e.target.value === '' ? null : parseInt(e.target.value));
                            setPageNumber(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Statuses</option>
                        {Object.entries(BATCH_STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
            </div>
            <button
                onClick={() => { setStatusFilter(null); setPageNumber(1); }}
                className="mt-4 text-sm text-blue-600 hover:underline"
            >
                Reset to default
            </button>
        </div>
    );

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    {/* Header */}
                    <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Report Requests</h1>
                            <p className="text-gray-500 text-sm sm:text-base">
                                Generate report batches as drafts, finalize them, then submit them for approval.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                            <Button
                                variant="success"
                                onClick={() => navigate('/admin/approval-requests/generate')}
                                className="w-full sm:w-auto flex items-center justify-center gap-2"
                            >
                                <Plus size={16} />
                                New Report
                            </Button>
                        </div>
                    </div>

                    <ReusableTable
                        title="Report Batches"
                        subTitle="Draft, finalize and submit your report batches for approval"
                        columns={columns}
                        data={batches}
                        getActions={getActions}
                        loading={loading}
                        showSearch={true}
                        onSearch={(q) => { setSearch(q); setPageNumber(1); }}
                        searchPlaceholder="Search batches..."
                        emptyMessage="No report batches yet. Create your first report!"
                        showFilter={true}
                        filterPanel={filterPanel}
                        onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                        isFilterOpen={isFilterOpen}
                    />

                    {!loading && totalRecords > 0 && (
                        <Pagination
                            currentPage={pageNumber}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            onPageChange={setPageNumber}
                            onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
                        />
                    )}

                    {/* Submit for Approval Confirmation */}
                    <ReportActionModal
                        isOpen={submitTarget !== null}
                        mode="submit"
                        onClose={() => setSubmitTarget(null)}
                        onConfirm={handleConfirmSubmit}
                        isSubmitting={submitting}
                        title={submitTarget?.title || APPROVAL_TYPE_LABELS[submitTarget?.reportType ?? -1] || 'Report Batch'}
                        subtitle={submitTarget?.periodLabel}
                        detail={submitTarget ? `${submitTarget.totalRecords} record${submitTarget.totalRecords === 1 ? '' : 's'}` : undefined}
                    />

                </div>
            </div>
        </div>
    );
}

export default ApprovalRequests;
